import React from "react";

export function PageHeader({ eyebrow, title, description, subtitle, actions }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
      <div>
        {eyebrow && <div className="label-overline mb-2">{eyebrow}</div>}
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-[var(--ink)]">{title}</h1>
        {(description || subtitle) && <p className="text-[var(--muted)] mt-2 text-sm max-w-2xl">{description || subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Btn({ variant = "primary", className = "", children, ...rest }) {
  const base = "inline-flex items-center justify-center gap-2 h-9 px-4 text-sm font-medium rounded-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white",
    secondary: "bg-[var(--surface)] hover:bg-[var(--row-hover)] text-[var(--ink)] border border-[var(--border)]",
    ghost: "text-[var(--muted)] hover:text-[var(--ink)] hover:bg-[var(--row-hover)]",
    danger: "bg-[var(--surface)] hover:bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30",
    accent: "bg-[var(--anthracite)] hover:bg-[#3E3E3E] text-white",
  };
  return <button className={`${base} ${variants[variant]} ${className}`} {...rest}>{children}</button>;
}

export function Field({ label, children, className = "", hint }) {
  return (
    <div className={className}>
      <label className="label-overline block mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-xs text-[var(--muted-2)] mt-1">{hint}</div>}
    </div>
  );
}

export function TextInput({ className = "", ...rest }) {
  return (
    <input
      className={`w-full h-10 px-3 bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm text-[var(--ink)] placeholder-[var(--muted-2)] focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)] focus:outline-none transition-colors ${className}`}
      {...rest}
    />
  );
}

export function TextArea({ className = "", ...rest }) {
  return (
    <textarea
      className={`w-full min-h-[84px] px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm text-[var(--ink)] placeholder-[var(--muted-2)] focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)] focus:outline-none transition-colors ${className}`}
      {...rest}
    />
  );
}

export function Select({ className = "", children, ...rest }) {
  return (
    <select
      className={`w-full h-10 px-3 bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm text-[var(--ink)] focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)] focus:outline-none transition-colors ${className}`}
      {...rest}
    >
      {children}
    </select>
  );
}

export function Section({ title, children, right }) {
  return (
    <section className="swiss-card p-6">
      {(title || right) && (
        <div className="flex items-center justify-between mb-4 gap-3">
          {title && <h3 className="font-heading text-lg font-medium">{title}</h3>}
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <div className="swiss-card p-12 text-center">
      <div className="label-overline">Empty</div>
      <h3 className="font-heading text-xl mt-2">{title}</h3>
      {description && <p className="text-[var(--muted)] mt-2 text-sm max-w-md mx-auto">{description}</p>}
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </div>
  );
}

const STATUS_PALETTE = {
  // quote statuses
  draft:     { bg: "bg-[var(--row-hover)]",        text: "text-[var(--muted)]",  dot: "bg-[var(--muted-2)]" },
  sent:      { bg: "bg-[var(--brand-soft)]",       text: "text-[var(--brand)]",  dot: "bg-[var(--brand)]" },
  accepted:  { bg: "bg-[#10B981]/10",              text: "text-[#047857]",       dot: "bg-[#10B981]" },
  rejected:  { bg: "bg-[#FF3B30]/10",              text: "text-[#C2261E]",       dot: "bg-[#FF3B30]" },
  // invoice statuses
  issued:    { bg: "bg-[var(--brand-soft)]",       text: "text-[var(--brand)]",  dot: "bg-[var(--brand)]" },
  paid:      { bg: "bg-[#10B981]/10",              text: "text-[#047857]",       dot: "bg-[#10B981]" },
  overdue:   { bg: "bg-[#F59E0B]/15",              text: "text-[#B45309]",       dot: "bg-[#F59E0B]" },
  cancelled: { bg: "bg-[#FF3B30]/10",              text: "text-[#C2261E]",       dot: "bg-[#FF3B30]" },
};

const STATUS_LABELS = {
  de: { draft: "Entwurf", sent: "Versendet", accepted: "Angenommen", rejected: "Abgelehnt",
        issued: "Ausgestellt", paid: "Bezahlt", overdue: "Überfällig", cancelled: "Storniert" },
  en: { draft: "Draft", sent: "Sent", accepted: "Accepted", rejected: "Rejected",
        issued: "Issued", paid: "Paid", overdue: "Overdue", cancelled: "Cancelled" },
};

export function StatusBadge({ status, lang = "de" }) {
  const p = STATUS_PALETTE[status] || STATUS_PALETTE.draft;
  const label = STATUS_LABELS[lang]?.[status] || STATUS_LABELS.en[status] || status;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-sm ${p.bg} ${p.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />
      {label}
    </span>
  );
}
